import React, { useState, useRef } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Switch } from '../ui/switch';
import { Save, Plus, Trash2, GripVertical, Menu, Eye, EyeOff, ArrowUp, ArrowDown, ExternalLink } from 'lucide-react';

const MenuTab = ({ menu, setMenu, saving, onSave }) => {
  const [dragIndex, setDragIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const dragNode = useRef(null);

  const sortedMenu = [...menu].sort((a, b) => a.order - b.order);

  const addMenuItem = () => {
    const newItem = {
      id: `menu-${Date.now()}`,
      label: 'New Page',
      url: '/',
      order: menu.length + 1,
      active: true
    };
    setMenu([...menu, newItem]);
  };

  const updateMenuItem = (id, field, value) => {
    setMenu(menu.map(item => item.id === id ? { ...item, [field]: value } : item));
  };

  const deleteMenuItem = (id) => {
    setMenu(menu.filter(item => item.id !== id));
  };

  const moveItem = (fromIdx, toIdx) => {
    if (toIdx < 0 || toIdx >= sortedMenu.length) return;
    const reordered = [...sortedMenu];
    const [moved] = reordered.splice(fromIdx, 1);
    reordered.splice(toIdx, 0, moved);
    setMenu(reordered.map((item, idx) => ({ ...item, order: idx + 1 })));
  };

  // Drag handlers
  const handleDragStart = (e, idx) => {
    dragNode.current = e.target;
    setDragIndex(idx);
    e.dataTransfer.effectAllowed = 'move';
    setTimeout(() => { if (dragNode.current) dragNode.current.style.opacity = '0.4'; }, 0);
  };

  const handleDragOver = (e, idx) => {
    e.preventDefault();
    if (dragIndex === null || dragIndex === idx) return;
    setDragOverIndex(idx);
  };

  const handleDragEnd = () => {
    if (dragNode.current) dragNode.current.style.opacity = '1';
    if (dragIndex !== null && dragOverIndex !== null && dragIndex !== dragOverIndex) {
      moveItem(dragIndex, dragOverIndex);
    }
    setDragIndex(null);
    setDragOverIndex(null);
    dragNode.current = null;
  };

  const activeCount = menu.filter(m => m.active).length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-zinc-900/60 rounded-xl border border-zinc-800/50 p-6">
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-blue-500/10 rounded-lg flex items-center justify-center">
              <Menu className="w-4 h-4 text-blue-400" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-white">Menu Management</h2>
              <p className="text-zinc-500 text-xs mt-0.5">
                {menu.length} menu items  ·  {activeCount} active
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button onClick={addMenuItem} variant="outline" size="sm" className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 h-8 text-xs">
              <Plus className="w-3.5 h-3.5 mr-1.5" />
              New Item
            </Button>
            <Button onClick={onSave} disabled={saving} size="sm" className="bg-emerald-600 hover:bg-emerald-700 h-8 text-xs">
              <Save className="w-3.5 h-3.5 mr-1.5" />
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </div>
      </div>

      {/* Info */}
      <div className="bg-blue-500/5 border border-blue-500/10 rounded-lg px-4 py-3 flex items-start gap-3">
        <GripVertical className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" />
        <p className="text-zinc-400 text-xs leading-relaxed">
          Reorder menu items by dragging or using arrow keys. Use the toggle to change active/inactive status.
        </p>
      </div>

      {/* Menu Items */}
      <div className="space-y-2">
        {sortedMenu.length === 0 ? (
          <div className="bg-zinc-900/60 rounded-xl border border-zinc-800/50 p-12 text-center">
            <Menu className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
            <p className="text-zinc-500 text-sm mb-4">No menu items added yet</p>
            <Button onClick={addMenuItem} size="sm" className="bg-emerald-600 hover:bg-emerald-700 h-8 text-xs">
              <Plus className="w-3.5 h-3.5 mr-1.5" />
              Add First Item
            </Button>
          </div>
        ) : (
          sortedMenu.map((item, idx) => (
            <div
              key={item.id}
              draggable
              onDragStart={(e) => handleDragStart(e, idx)}
              onDragOver={(e) => handleDragOver(e, idx)}
              onDragEnd={handleDragEnd}
              className={`bg-zinc-900/60 rounded-xl border transition-all group ${
                dragOverIndex === idx && dragIndex !== idx
                  ? 'border-emerald-500/50 bg-emerald-500/5'
                  : item.active
                    ? 'border-zinc-800/50 hover:border-zinc-700/50'
                    : 'border-zinc-800/30 opacity-60 hover:opacity-80'
              }`}
            >
              <div className="flex items-center gap-3 p-3">
                {/* Drag Handle */}
                <div className="cursor-grab active:cursor-grabbing text-zinc-600 hover:text-zinc-400 transition-colors p-1">
                  <GripVertical className="w-4 h-4" />
                </div>

                {/* Order Number */}
                <div className="w-6 h-6 rounded-md bg-zinc-800 flex items-center justify-center text-zinc-500 text-xs font-mono flex-shrink-0">
                  {idx + 1}
                </div>

                {/* Label */}
                <div className="flex-1 min-w-0">
                  <Input
                    value={item.label}
                    onChange={(e) => updateMenuItem(item.id, 'label', e.target.value)}
                    className="bg-zinc-800/50 border-zinc-700/50 text-white h-8 text-sm"
                    placeholder="Menu name"
                  />
                </div>

                {/* URL */}
                <div className="flex-1 min-w-0">
                  <div className="relative">
                    <Input
                      value={item.url}
                      onChange={(e) => updateMenuItem(item.id, 'url', e.target.value)}
                      className="bg-zinc-800/50 border-zinc-700/50 text-white h-8 text-sm pr-8"
                      placeholder="/page-path"
                    />
                    <ExternalLink className="w-3 h-3 text-zinc-600 absolute right-2.5 top-1/2 -translate-y-1/2" />
                  </div>
                </div>

                {/* Move Arrows */}
                <div className="flex flex-col gap-0.5">
                  <button
                    onClick={() => moveItem(idx, idx - 1)}
                    disabled={idx === 0}
                    className="w-6 h-4 rounded flex items-center justify-center text-zinc-600 hover:text-white hover:bg-zinc-800 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  >
                    <ArrowUp className="w-3 h-3" />
                  </button>
                  <button
                    onClick={() => moveItem(idx, idx + 1)}
                    disabled={idx === sortedMenu.length - 1}
                    className="w-6 h-4 rounded flex items-center justify-center text-zinc-600 hover:text-white hover:bg-zinc-800 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  >
                    <ArrowDown className="w-3 h-3" />
                  </button>
                </div>

                {/* Active Toggle */}
                <div className="flex items-center gap-2 px-2">
                  {item.active ? (
                    <Eye className="w-3.5 h-3.5 text-emerald-400" />
                  ) : (
                    <EyeOff className="w-3.5 h-3.5 text-zinc-600" />
                  )}
                  <Switch
                    checked={item.active}
                    onCheckedChange={(checked) => updateMenuItem(item.id, 'active', checked)}
                    className="data-[state=checked]:bg-emerald-500"
                  />
                </div>

                {/* Delete */}
                <button
                  onClick={() => deleteMenuItem(item.id)}
                  className="p-1.5 rounded-md text-zinc-600 hover:text-red-400 hover:bg-red-500/10 transition-colors"
                  title="Delete"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Preview */}
      {sortedMenu.length > 0 && (
        <div className="bg-zinc-900/60 rounded-xl border border-zinc-800/50 p-5">
          <h3 className="text-xs font-semibold text-zinc-500 uppercase tracking-wider mb-3">Menu Preview</h3>
          <div className="bg-zinc-800/50 rounded-lg p-3 flex items-center gap-1 flex-wrap">
            {sortedMenu.filter(m => m.active).map((item) => (
              <span key={item.id} className="px-3 py-1.5 rounded-md bg-zinc-700/50 text-zinc-300 text-sm hover:text-white transition-colors">
                {item.label}
              </span>
            ))}
            {sortedMenu.filter(m => m.active).length === 0 && (
              <p className="text-zinc-600 text-sm">No active menu items</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default MenuTab;
